import dayjs, { type Dayjs } from 'dayjs';
import { useGoodsOptionsBrief } from '../queries/useGoodsOptionsBrief';
import { useCallback, useMemo } from 'react';
import { useConfigs, useMyInfo } from 'shared/src/queries';
import {
  getCurrentTimeDiff,
  getDayOfWeek,
  getHour,
  isNil,
} from 'shared/src/utils';
import {
  findNearestTodayDeliveryOperatingDay,
  getPastTodayDeliveryDeadlineText,
  isTodayDeliveryOperatingDay,
} from './orderDeadlineTextUtils';

export type OrderDeadline = {
  text: string;
  isTimeOver: boolean;
};

type OrderDeadlineViewType = 'option' | 'optionInline';

const EMPTY_DEADLINE: OrderDeadline = { text: '', isTimeOver: false };

const RURAL_REGION_ADDITION_DAYS = 2;

const formatRuralArrival = (arrivalDate: Dayjs): string =>
  `${arrivalDate.format('M/D')}(${getDayOfWeek(arrivalDate)}) 도착`;

const getThirdPLOptionDeadline = (
  limitTime: Dayjs,
  isDefaultAddressRural: boolean,
): OrderDeadline => {
  // 23:59:59는 자정으로 취급 → 평일·토 자정 컷오프이므로 "컷오프 후" 상태가 발생하지 않음
  const isMidnight = limitTime.format('HH:mm:ss') === '23:59:59';
  const isTimeOver = !isMidnight && getCurrentTimeDiff(limitTime) > 0;

  if (isDefaultAddressRural) {
    // 컷오프 이전이면 내일 도착(today + 1), 이후면 모레 도착(today + 2) 가정 + 도서산간 +2일
    const baseOffsetDays = isTimeOver ? 2 : 1;
    const arrivalDay = dayjs().add(
      baseOffsetDays + RURAL_REGION_ADDITION_DAYS,
      'day',
    );
    const arrivalText = formatRuralArrival(arrivalDay);
    // 도서산간은 컷오프 시각 안내 없이 항상 "지금 주문하면 {도착일} 도착"
    return { text: `지금 주문하면 ${arrivalText}`, isTimeOver };
  }

  if (isMidnight) {
    return { text: '밤 12시 전 주문 시 내일 도착', isTimeOver: false };
  }

  // 컷오프 이후
  if (isTimeOver) {
    const arrivalDay = dayjs().add(2, 'day');
    return {
      text: `지금 주문하면 모레 ${arrivalDay.format('M/D')}(${getDayOfWeek(arrivalDay)}) 도착`,
      isTimeOver: true,
    };
  }

  // 컷오프 이전
  const timeText = limitTime.locale('ko').format('A h');
  return { text: `${timeText}시 전 주문 시 내일 도착`, isTimeOver: false };
};

export const useGetOrderDeadlineText = () => {
  const { data: optionsBrief } = useGoodsOptionsBrief();
  const { data: configs } = useConfigs();
  const { data: myInfo } = useMyInfo();
  const isDefaultAddressRural = myInfo?.is_default_address_rural === true;
  const deliveryHolidays = useMemo(
    () => configs?.delivery_holidays ?? [],
    [configs?.delivery_holidays],
  );

  const getLimitTime = useCallback(
    (params: { delivery_type: 'shak' | 'today' | 'third_pl' }) => {
      if (isNil(optionsBrief)) return null;
      // configs?.shak_delivery.operation_end_time
      if (params.delivery_type === 'shak') {
        return getHour(configs?.shak_delivery.operation_end_time);
      }

      if (params.delivery_type === 'third_pl') {
        if (isNil(optionsBrief.third_pl_delivery_order_deadline_time)) {
          return null;
        }
        return getHour(optionsBrief.third_pl_delivery_order_deadline_time);
      }

      return getHour(optionsBrief.today_delivery_order_deadline_time);
    },
    [configs?.shak_delivery.operation_end_time, optionsBrief],
  );

  return useCallback<
    (params: {
      delivery_type: 'shak' | 'today' | 'third_pl' | 'standard';
      viewType?: OrderDeadlineViewType;
    }) => OrderDeadline
  >(
    params => {
      if (isNil(optionsBrief)) {
        return EMPTY_DEADLINE;
      }

      if (params.delivery_type === 'standard') {
        return EMPTY_DEADLINE;
      }

      if (params.delivery_type === 'third_pl') {
        const limitTime = getLimitTime({ delivery_type: 'third_pl' });
        if (isNil(limitTime)) return EMPTY_DEADLINE;

        return getThirdPLOptionDeadline(limitTime, isDefaultAddressRural);
      }

      const limitTime = getLimitTime({ delivery_type: params.delivery_type });
      if (isNil(limitTime)) return EMPTY_DEADLINE;

      const isInline = params.viewType === 'optionInline';
      const now = dayjs();
      const isTimeOver = isInline
        ? now.diff(limitTime) > 0 ||
          !isTodayDeliveryOperatingDay(now, deliveryHolidays)
        : getCurrentTimeDiff(limitTime) > 0;

      if (isTimeOver) {
        // 다음 "출발일" 은 운영일(평일 - 배송 휴무일) 중 가장 가까운 날이다.
        // 내일이 토/일/공휴일이면 그날 출발할 수 없으므로 건너뛴다(연휴도 loop 로 처리).
        // 기존에는 inline 이 아닌 경우 '내일 출발' 이 문자열로 박혀 있어, 금요일 마감 후엔
        // 실제로는 월요일 출발인데 '내일 출발'(=토요일)로 안내됐다.
        const nearestOperatingDay = findNearestTodayDeliveryOperatingDay(
          now,
          deliveryHolidays,
        );

        return {
          text: getPastTodayDeliveryDeadlineText(nearestOperatingDay, now),
          isTimeOver: true,
        };
      }

      const hour = limitTime.hour();
      return {
        text: `${hour < 12 ? '오전 ' : '오후 '}${hour > 12 ? hour - 12 : hour}시 전 주문하면 오늘 출발`,
        isTimeOver: false,
      };
    },
    [deliveryHolidays, getLimitTime, optionsBrief, isDefaultAddressRural],
  );
};

export const useGetInlineOrderDeadlineText = () => {
  const getOrderDeadlineText = useGetOrderDeadlineText();

  return useCallback(
    (params: { delivery_type: 'shak' | 'today' | 'third_pl' | 'standard' }) =>
      getOrderDeadlineText({
        ...params,
        viewType: 'optionInline',
      }),
    [getOrderDeadlineText],
  );
};
